/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/refund
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import { graphqlMoneyBagFieldToRest } from "./constants";
import {
  ShopifyOrderAdjustmentKey,
  ShopifyOrderAdjustmentToOrderKey,
  ShopifyOrderAdjustmentToRefundKey,
  ShopifyOrderAdjustmentToShopKey,
  ShopifyOrderKey,
  ShopifyRefundKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyOrderAdjustmentKey,
  "2022-01": {
    name: "Shopify Order Adjustment",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderAdjustmentToOrderKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "order_id" } },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Refund",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderAdjustmentToRefundKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "refund_id" } },
        configuration: {
          relatedModelKey: ShopifyRefundKey,
        },
      },
      {
        name: "Amount",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "amount" } },
      },
      {
        name: "Tax Amount",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_amount" } },
      },
      {
        name: "Kind",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "kind" } },
      },
      {
        name: "Reason",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "reason" } },
      },
      {
        name: "Amount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "amount_set" } },
      },
      {
        name: "Tax Amount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_amount_set" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderAdjustmentToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: "order" },
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderAdjustmentToOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "order_id", graphQLFieldKey: "orderId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Refund",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderAdjustmentToRefundKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "refund_id", graphQLFieldKey: "refundId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyRefundKey,
        },
      },
      {
        name: "Amount",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "amount", graphQLFieldKey: "amount", skipGraphQLSelection: true },
        },
      },
      {
        name: "Tax Amount",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "tax_amount", graphQLFieldKey: "taxAmount", skipGraphQLSelection: true },
        },
      },
      {
        name: "Kind",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "kind", deprecated: true } },
      },
      {
        name: "Reason",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "reason", graphQLFieldKey: "reason", isShopifyEnum: true } },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [
            { name: "CUSTOMER" },
            { name: "DAMAGE" },
            { name: "FULL_RETURN_BALANCING_ADJUSTMENT" },
            { name: "PENDING_REFUND_DISCREPANCY" },
            { name: "REFUND_DISCREPANCY" },
            { name: "RESTOCK" },
          ],
        },
      },
      {
        name: "Amount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "amount_set",
            graphQLFieldKey: "amountSet",
            graphQLSelection: graphqlMoneyBagFieldToRest,
          },
        },
      },
      {
        name: "Tax Amount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "tax_amount_set",
            graphQLFieldKey: "taxAmountSet",
            graphQLSelection: graphqlMoneyBagFieldToRest,
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "order.refunds",
      resource: "refund.orderAdjustments",
      additionalFields: ["orderId:order { id }", "refundId:id"],
    },
  },
};

export const ShopifyOrderAdjustment = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
